import React, { useState, useEffect, useCallback } from 'react';
import plApi from '../services/pl';
import './PL.css';

const MOIS = ['Jan','Fev','Mar','Avr','Mai','Jun','Jul','Aou','Sep','Oct','Nov','Dec'];

function fmt(n) {
  if (n === null || n === undefined) return '-';
  return Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function PL({ user, isAdmin }) {
  const [activeTab, setActiveTab] = useState('reporting');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Filters
  const [exercices, setExercices] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [activites, setActivites] = useState([]);
  const [selectedExercice, setSelectedExercice] = useState('');
  const [selectedSocietes, setSelectedSocietes] = useState([]);
  const [selectedActivite, setSelectedActivite] = useState('global');

  // Reporting data
  const [comptes, setComptes] = useState([]);
  const [reportingData, setReportingData] = useState({ reel: [], provisions: [], budget: [] });
  const [expandedRows, setExpandedRows] = useState({});

  // Fournisseurs data
  const [fournisseursData, setFournisseursData] = useState({ reel: [], provisions: [] });

  // Salaries data (P&L tab)
  const [salaries, setSalaries] = useState([]);
  const [salariesDetails, setSalariesDetails] = useState([]);
  const [typesChargesRH, setTypesChargesRH] = useState([]);

  // Config panel
  const [configOpen, setConfigOpen] = useState(false);
  const [configTab, setConfigTab] = useState('societes');
  const [configSocietes, setConfigSocietes] = useState([]);
  const [configActivites, setConfigActivites] = useState([]);
  const [configExercices, setConfigExercices] = useState([]);
  const [configComptes, setConfigComptes] = useState([]);
  const [configTypesRH, setConfigTypesRH] = useState([]);
  const [configPostes, setConfigPostes] = useState([]);
  const [configServices, setConfigServices] = useState([]);
  const [configEtabs, setConfigEtabs] = useState([]);
  const [configGroupes, setConfigGroupes] = useState([]);
  const [newGroupe, setNewGroupe] = useState('');

  // Inline edit
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  // Config forms
  const [newSociete, setNewSociete] = useState('');
  const [newActivite, setNewActivite] = useState('');
  const [newPoste, setNewPoste] = useState({ libelle: '', activite_id: '' });
  const [newExercice, setNewExercice] = useState({ annee: '' });
  const [newCompte, setNewCompte] = useState({ libelle: '', type_ligne: 'detail', section: '', parent_id: '', signe: 1 });
  const [newTypeRH, setNewTypeRH] = useState('');
  const [newService, setNewService] = useState({ libelle: '', activite_id: '' });

  const showToast = useCallback((msg, type = 'success', undoAction = null) => {
    setToast({ msg, type, undoAction });
    if (!undoAction) {
      setTimeout(() => setToast(null), 3000);
    } else {
      setTimeout(() => setToast(null), 8000);
    }
  }, []);

  // Load filters
  useEffect(() => {
    Promise.all([plApi.getExercices(), plApi.getSocietes(), plApi.getActivites()])
      .then(([exRes, soRes, acRes]) => {
        setExercices(exRes.data);
        setSocietes(soRes.data);
        setActivites(acRes.data);
        if (exRes.data.length > 0) setSelectedExercice(exRes.data[0].id);
        if (soRes.data.length > 0) setSelectedSocietes(soRes.data.map(s => s.id));
      }).catch(() => showToast('Erreur chargement filtres', 'error'));
  }, [showToast]);

  // Load plan de comptes
  useEffect(() => {
    plApi.getPlanComptes().then(res => setComptes(res.data)).catch(() => {});
  }, []);

  // Load reporting data
  useEffect(() => {
    if (!selectedExercice || selectedSocietes.length === 0) return;
    setLoading(true);
    const params = {
      exercice_id: selectedExercice,
      societe_ids: selectedSocietes.join(','),
      activite_id: selectedActivite
    };
    const promises = [plApi.getReporting(params)];
    if (activeTab === 'fournisseurs') promises.push(plApi.getDetailFournisseurs(params));
    if (activeTab === 'salaries') {
      const salApi = require('../services/salaires').default;
      promises.push(salApi.getSalaries(params));
      promises.push(salApi.getDetails(params));
      promises.push(salApi.getTypesCharges());
    }
    Promise.all(promises).then(results => {
      setReportingData(results[0].data);
      if (activeTab === 'fournisseurs' && results[1]) setFournisseursData(results[1].data);
      if (activeTab === 'salaries') {
        if (results[1]) setSalaries(results[1].data);
        if (results[2]) setSalariesDetails(results[2].data);
        if (results[3]) setTypesChargesRH(results[3].data);
      }
    }).catch(() => showToast('Erreur chargement donnees', 'error'))
      .finally(() => setLoading(false));
  }, [selectedExercice, selectedSocietes, selectedActivite, activeTab, showToast]);

  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));

  // Export / Print P&L
  const handleExportPL = () => {
    const exercice = exercices.find(e => String(e.id) === String(selectedExercice));
    const tableEl = document.querySelector('.pl-reporting-table');
    if (!tableEl) return;
    const printWin = window.open('', '_blank', 'width=1200,height=800');
    if (!printWin) { showToast('Popup bloquee — autorisez les popups', 'error'); return; }
    printWin.document.write(`<!DOCTYPE html><html><head><title>P&L ${exercice?.annee || ''}</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 20px; font-size: 11px; }
        h1 { font-size: 16px; margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 4px 8px; border: 1px solid #d1d5db; text-align: right; white-space: nowrap; }
        th { background: #f3f4f6; font-weight: 600; text-align: center; }
        td:first-child, th:first-child { text-align: left; }
        .row-total td { font-weight: 700; background: #f3f4f6; }
        .row-sous-total td { font-weight: 600; background: #fafaf8; }
        @media print { body { padding: 0; } }
      </style></head><body>
      <h1>P&L — ${exercice?.annee || ''}</h1>
      ${tableEl.outerHTML}
      <script>window.print();</script>
    </body></html>`);
    printWin.document.close();
  };

  // Keyboard: Escape closes config panel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && configOpen) setConfigOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [configOpen]);

  const handleBudgetEdit = async (compte_id, mois) => {
    try {
      await plApi.saveBudget({
        exercice_id: selectedExercice,
        societe_id: selectedSocietes[0],
        activite_id: selectedActivite === 'global' ? null : selectedActivite,
        compte_id, mois, montant: parseFloat(editingValue) || 0
      });
      showToast('Budget modifie');
      setEditingCell(null);
      const params = { exercice_id: selectedExercice, societe_ids: selectedSocietes.join(','), activite_id: selectedActivite };
      const res = await plApi.getReporting(params);
      setReportingData(res.data);
    } catch { showToast('Erreur modification', 'error'); }
  };

  // Config loaders
  const loadConfig = useCallback(async () => {
    try {
      const [s, a, e, c, t, p, sv, et, gr] = await Promise.all([
        plApi.getSocietes(), plApi.getActivites(), plApi.getExercices(),
        plApi.getPlanComptes(), (() => { const salApi = require('../services/salaires').default; return salApi.getTypesCharges(); })(),
        plApi.getPostes(), plApi.getServices(), plApi.getEtablissements(), plApi.getGroupes()
      ]);
      setConfigSocietes(s.data);
      setConfigActivites(a.data);
      setConfigExercices(e.data);
      setConfigComptes(c.data);
      setConfigTypesRH(t.data);
      setConfigPostes(p.data);
      setConfigServices(sv.data);
      if (et) setConfigEtabs(et.data);
      if (gr) setConfigGroupes(gr.data);
    } catch { showToast('Erreur chargement config', 'error'); }
  }, [showToast]);

  const openConfig = () => { setConfigOpen(true); loadConfig(); };

  const handleAddSociete = async () => { if (!newSociete.trim()) return; try { await plApi.createSociete({ nom: newSociete }); setNewSociete(''); loadConfig(); showToast('Societe ajoutee'); } catch { showToast('Erreur', 'error'); } };
  const handleAddActivite = async () => { if (!newActivite.trim()) return; try { await plApi.createActivite({ code: newActivite.trim(), libelle: newActivite.trim() }); setNewActivite(''); loadConfig(); showToast('Activite ajoutee'); } catch { showToast('Erreur', 'error'); } };
  const handleAddExercice = async () => { if (!newExercice.annee) return; try { await plApi.createExercice(newExercice); setNewExercice({ annee: '' }); loadConfig(); showToast('Exercice ajoute'); } catch { showToast('Erreur', 'error'); } };
  const handleAddCompte = async () => { if (!newCompte.libelle) return; try { await plApi.createCompte(newCompte); setNewCompte({ libelle: '', type_ligne: 'detail', section: '', parent_id: '', signe: 1 }); loadConfig(); showToast('Compte ajoute'); } catch { showToast('Erreur', 'error'); } };
  const handleAddTypeRH = async () => { if (!newTypeRH.trim()) return; try { const salApi = require('../services/salaires').default; await salApi.createTypeCharge({ libelle: newTypeRH }); setNewTypeRH(''); loadConfig(); showToast('Type ajoute'); } catch { showToast('Erreur', 'error'); } };

  const handleDeleteSociete = async (id) => { try { await plApi.deleteSociete(id); loadConfig(); showToast('Supprime'); } catch { showToast('Erreur', 'error'); } };
  const handleDeleteActivite = async (id) => { try { await plApi.deleteActivite(id); loadConfig(); showToast('Supprime'); } catch { showToast('Erreur', 'error'); } };
  const handleDeleteCompte = async (id) => { try { await plApi.deleteCompte(id); loadConfig(); showToast('Supprime'); } catch { showToast('Erreur', 'error'); } };
  const handleDeleteTypeRH = async (id) => { try { const salApi = require('../services/salaires').default; await salApi.deleteTypeCharge(id); loadConfig(); showToast('Supprime'); } catch { showToast('Erreur', 'error'); } };
  const handleAddPoste = async () => { if (!newPoste.libelle.trim() || !newPoste.activite_id) return; try { await plApi.createPoste({ libelle: newPoste.libelle.trim(), activite_id: newPoste.activite_id }); setNewPoste({ libelle: '', activite_id: '' }); loadConfig(); showToast('Poste ajoute'); } catch { showToast('Erreur', 'error'); } };
  const handleDeletePoste = async (id) => { try { await plApi.deletePoste(id); loadConfig(); showToast('Supprime'); } catch { showToast('Erreur', 'error'); } };
  const handleAddGroupe = async () => {
    if (!newGroupe.trim()) return;
    try { await plApi.createGroupe({ nom: newGroupe.trim() }); setNewGroupe(''); loadConfig(); showToast('Groupe ajoute'); }
    catch { showToast('Erreur', 'error'); }
  };

  const handleAddService = async () => { if (!newService.libelle.trim() || !newService.activite_id) return; try { await plApi.createService({ libelle: newService.libelle.trim(), activite_id: newService.activite_id }); setNewService({ libelle: '', activite_id: '' }); loadConfig(); showToast('Service ajoute'); } catch { showToast('Erreur', 'error'); } };
  const handleDeleteService = async (id) => { try { await plApi.deleteService(id); loadConfig(); showToast('Supprime'); } catch { showToast('Erreur', 'error'); } };

  // Build tree
  const buildTree = (list) => {
    const map = {};
    const roots = [];
    list.forEach(c => { map[c.id] = { ...c, children: [] }; });
    list.forEach(c => {
      if (c.parent_id && map[c.parent_id]) map[c.parent_id].children.push(map[c.id]);
      else roots.push(map[c.id]);
    });
    return roots;
  };

  const getReelForCompte = (compteId, mois) => {
    const r = reportingData.reel?.find(x => x.compte_id === compteId && x.mois === mois);
    return r ? r.montant_reel : null;
  };
  const getProvisionForCompte = (compteId, mois) => {
    const p = reportingData.provisions?.find(x => x.compte_id === compteId && x.mois === mois);
    return p || null;
  };
  const getBudgetForCompte = (compteId, mois) => {
    const b = reportingData.budget?.find(x => x.compte_id === compteId && x.mois === mois);
    return b ? b.montant_budget : null;
  };

  const tree = buildTree(comptes);

  // Navigate to next/prev budget cell with Tab/Shift+Tab
  const getEditableCells = useCallback(() => {
    const cells = [];
    const walkTree = (nodes) => {
      nodes.forEach(c => {
        if (c.type_ligne === 'detail') {
          MOIS.forEach((_, mi) => cells.push({ compteId: c.id, mois: mi + 1 }));
        }
        if (c.children) walkTree(c.children);
      });
    };
    walkTree(tree);
    return cells;
  }, [tree]);

  const navigateBudgetCell = (currentCompteId, currentMois, direction) => {
    const cells = getEditableCells();
    const idx = cells.findIndex(c => c.compteId === currentCompteId && c.mois === currentMois);
    if (idx === -1) return;
    const nextIdx = direction === 'next' ? idx + 1 : idx - 1;
    if (nextIdx >= 0 && nextIdx < cells.length) {
      const next = cells[nextIdx];
      const budget = getBudgetForCompte(next.compteId, next.mois);
      setEditingCell(`budget-${next.compteId}-${next.mois}`);
      setEditingValue(budget || '');
    }
  };

  const renderReportingRow = (compte, depth = 0) => {
    const isParent = compte.children && compte.children.length > 0;
    const isExpanded = expandedRows[compte.id];
    const rowClass = compte.type_ligne === 'total' ? 'row-total' : compte.type_ligne === 'sous-total' ? 'row-sous-total' : compte.type_ligne === 'pourcentage' ? 'row-pourcentage' : '';

    return (
      <React.Fragment key={compte.id}>
        <tr className={rowClass}>
          <td className="pl-tree-label" style={{ paddingLeft: `${depth * 20 + 8}px` }}>
            {isParent && (
              <span className="pl-tree-toggle" onClick={() => toggleRow(compte.id)}>
                {isExpanded ? '\u25BE' : '\u25B8'}
              </span>
            )}
            {compte.libelle}
          </td>
          {MOIS.map((_, mi) => {
            const mois = mi + 1;
            const reel = getReelForCompte(compte.id, mois);
            const prov = getProvisionForCompte(compte.id, mois);
            const budget = getBudgetForCompte(compte.id, mois);
            return (
              <td key={mi} className="pl-reporting-cell">
                <div className="cell-reel">{fmt(reel)}</div>
                <div className="cell-provision">
                  {prov ? (
                    <span className={prov.has_alerte ? 'pl-provision-alerte' : 'pl-provision-ok'}>
                      {fmt(prov.montant_provision)}
                    </span>
                  ) : '-'}
                </div>
                <div className="cell-budget">
                  {editingCell === `budget-${compte.id}-${mois}` ? (
                    <input
                      className="pl-inline-edit"
                      type="number"
                      value={editingValue}
                      onChange={e => setEditingValue(e.target.value)}
                      onBlur={() => setEditingCell(null)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); handleBudgetEdit(compte.id, mois); }
                        if (e.key === 'Escape') setEditingCell(null);
                        if (e.key === 'Tab') { e.preventDefault(); handleBudgetEdit(compte.id, mois); navigateBudgetCell(compte.id, mois, e.shiftKey ? 'prev' : 'next'); }
                      }}
                      autoFocus
                    />
                  ) : (
                    <span
                      className={isAdmin ? 'pl-editable' : ''}
                      onClick={() => {
                        if (!isAdmin) return;
                        setEditingCell(`budget-${compte.id}-${mois}`);
                        setEditingValue(budget || '');
                      }}
                    >
                      {fmt(budget)}
                    </span>
                  )}
                </div>
              </td>
            );
          })}
        </tr>
        {isParent && isExpanded && compte.children.map(child => renderReportingRow(child, depth + 1))}
      </React.Fragment>
    );
  };

  const renderReporting = () => (
    <div className="pl-table-container">
      <div className="pl-table-scroll">
        <table className="pl-reporting-table">
          <thead>
            <tr>
              <th>Compte</th>
              {MOIS.map(m => <th key={m}>{m}</th>)}
            </tr>
            <tr className="pl-reporting-subheader">
              <th></th>
              {MOIS.map(m => (
                <th key={m}>
                  <span className="cell-reel">R</span>
                  <span className="cell-provision">P</span>
                  <span className="cell-budget">B</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tree.length === 0 ? (
              <tr><td colSpan={13} className="pl-empty-state">Aucun compte configure</td></tr>
            ) : tree.map(c => renderReportingRow(c))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderFournisseurs = () => {
    const allFournisseurs = [...new Set([
      ...(fournisseursData.reel || []).map(r => r.fournisseur),
      ...(fournisseursData.provisions || []).map(p => p.fournisseur)
    ])].sort();

    const comptesWithData = [...new Set([
      ...(fournisseursData.reel || []).map(r => r.compte_id),
      ...(fournisseursData.provisions || []).map(p => p.compte_id)
    ])];

    return (
      <div className="pl-table-container">
        <div className="pl-table-scroll">
          {comptesWithData.length === 0 ? (
            <div className="pl-empty-state">Aucune donnee fournisseur</div>
          ) : comptesWithData.map(compteId => {
            const compte = comptes.find(c => c.id === compteId);
            const fournisseurs = allFournisseurs.filter(f =>
              (fournisseursData.reel || []).some(r => r.compte_id === compteId && r.fournisseur === f) ||
              (fournisseursData.provisions || []).some(p => p.compte_id === compteId && p.fournisseur === f)
            );
            return (
              <div key={compteId} style={{ marginBottom: 24 }}>
                <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>{compte ? compte.libelle : compteId}</h3>
                <table className="pl-fournisseur-table">
                  <thead>
                    <tr>
                      <th>Fournisseur</th>
                      {MOIS.map(m => <th key={m}>{m}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {fournisseurs.map(f => (
                      <tr key={f}>
                        <td>{f}</td>
                        {MOIS.map((_, mi) => {
                          const mois = mi + 1;
                          const reel = (fournisseursData.reel || []).find(r => r.compte_id === compteId && r.fournisseur === f && r.mois === mois);
                          const prov = (fournisseursData.provisions || []).find(p => p.compte_id === compteId && p.fournisseur === f && p.mois === mois);
                          return (
                            <td key={mi}>
                              <div className="cell-reel">{reel ? fmt(reel.montant) : '-'}</div>
                              {prov && (
                                <div className="cell-provision">
                                  <span className={`pl-provision-badge ${prov.statut_revue === 'ok' ? 'pl-provision-ok' : 'pl-provision-alerte'}`}>
                                    {fmt(prov.montant)}
                                  </span>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSalaries = () => (
    <div className="pl-table-container">
      <div className="pl-table-scroll">
        <table className="pl-salaries-table">
          <thead>
            <tr>
              <th>Salarie</th>
              <th>Type charge</th>
              {MOIS.map(m => <th key={m}>{m}</th>)}
            </tr>
          </thead>
          <tbody>
            {salaries.length === 0 ? (
              <tr><td colSpan={14} className="pl-empty-state">Aucun salarie</td></tr>
            ) : salaries.map(s => {
              const details = salariesDetails.filter(d => d.salarie_id === s.id);
              const chargeTypes = [...new Set(details.map(d => d.type_charge_id))];
              return chargeTypes.length === 0 ? (
                <tr key={s.id}>
                  <td>{s.nom} {s.prenom}</td>
                  <td>-</td>
                  {MOIS.map(m => <td key={m}>-</td>)}
                </tr>
              ) : chargeTypes.map((tcId, i) => {
                const tc = typesChargesRH.find(t => t.id === tcId);
                return (
                  <tr key={`${s.id}-${tcId}`}>
                    {i === 0 && <td rowSpan={chargeTypes.length}>{s.nom} {s.prenom}</td>}
                    <td>{tc ? tc.libelle : tcId}</td>
                    {MOIS.map((_, mi) => {
                      const mois = mi + 1;
                      const det = details.find(d => d.type_charge_id === tcId && d.mois === mois);
                      return <td key={mi}>{det ? fmt(det.montant) : '-'}</td>;
                    })}
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderConfigCompte = (compte, depth) => (
    <div key={compte.id} className="pl-tree-item" style={{ paddingLeft: depth * 20 }}>
      <span>{compte.libelle}</span>
      <span className="pl-tree-type">{compte.type_ligne}</span>
      {isAdmin && <button className="pl-config-delete" onClick={() => handleDeleteCompte(compte.id)}>x</button>}
      {compte.children && compte.children.map(child => renderConfigCompte(child, depth + 1))}
    </div>
  );

  const renderConfigPanel = () => (
    <div className={`pl-config-overlay ${configOpen ? 'open' : ''}`} onClick={() => setConfigOpen(false)}>
      <div className="pl-config-panel" onClick={e => e.stopPropagation()}>
        <div className="pl-config-header">
          <h2>Configuration</h2>
          <button className="pl-config-close" onClick={() => setConfigOpen(false)}>x</button>
        </div>
        <div className="pl-config-tabs">
          {[
            { id: 'groupes', label: 'Groupes' },
            { id: 'societes', label: 'Societes' },
            { id: 'activites', label: 'Activites' },
            { id: 'postes', label: 'Postes' },
            { id: 'services', label: 'Services' },
            { id: 'etablissements', label: 'Etablissements' },
            { id: 'exercices', label: 'Exercices' },
            { id: 'comptes', label: 'Plan de comptes' },
            { id: 'typesrh', label: 'Types charges RH' },
          ].map(t => (
            <button key={t.id} className={`pl-config-tab ${configTab === t.id ? 'active' : ''}`} onClick={() => setConfigTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="pl-config-content">
          {configTab === 'societes' && (
            <>
              {isAdmin && (
                <div className="pl-config-add-row">
                  <input value={newSociete} onChange={e => setNewSociete(e.target.value)} placeholder="Nom de la societe" />
                  <button onClick={handleAddSociete}>Ajouter</button>
                </div>
              )}
              <ul className="pl-config-list">
                {configSocietes.map(s => (
                  <li key={s.id} className="pl-societe-ligne">
                    <span>{s.nom}</span>
                    {isAdmin ? (
                      <select className="pl-societe-groupe" value={s.groupe_id || ''} title="Groupe de rattachement"
                        onChange={async e => {
                          try {
                            await plApi.updateSociete(s.id, { groupe_id: e.target.value ? parseInt(e.target.value) : null });
                            loadConfig();
                            showToast('Groupe modifie');
                          } catch { showToast('Erreur', 'error'); }
                        }}>
                        <option value="">Sans groupe</option>
                        {configGroupes.map(g => <option key={g.id} value={g.id}>{g.nom}</option>)}
                      </select>
                    ) : (
                      <span className="pl-etab-effectif">{s.groupe_nom || '—'}</span>
                    )}
                    {isAdmin && <button className="pl-config-delete" onClick={() => handleDeleteSociete(s.id)}>x</button>}
                  </li>
                ))}
              </ul>
            </>
          )}
          {configTab === 'activites' && (
            <>
              {isAdmin && (
                <div className="pl-config-add-row">
                  <input value={newActivite} onChange={e => setNewActivite(e.target.value)} placeholder="Nom de l'activite" />
                  <button onClick={handleAddActivite}>Ajouter</button>
                </div>
              )}
              <ul className="pl-config-list">
                {configActivites.map(a => (
                  <li key={a.id}>
                    <span>{a.libelle || a.code}</span>
                    {isAdmin && <button className="pl-config-delete" onClick={() => handleDeleteActivite(a.id)}>x</button>}
                  </li>
                ))}
              </ul>
            </>
          )}
          {configTab === 'postes' && (
            <>
              {isAdmin && (
                <div className="pl-config-add-row">
                  <select value={newPoste.activite_id} onChange={e => setNewPoste(p => ({ ...p, activite_id: e.target.value }))}>
                    <option value="">Activite *</option>
                    {configActivites.map(a => <option key={a.id} value={a.id}>{a.libelle}</option>)}
                  </select>
                  <input value={newPoste.libelle} onChange={e => setNewPoste(p => ({ ...p, libelle: e.target.value }))} placeholder="Nom du poste" />
                  <button onClick={handleAddPoste}>Ajouter</button>
                </div>
              )}
              <ul className="pl-config-list">
                {configActivites.map(act => {
                  const actPostes = configPostes.filter(p => p.activite_id === act.id);
                  if (actPostes.length === 0) return null;
                  return (
                    <li key={`act-${act.id}`} className="pl-config-group-header">
                      <strong>{act.libelle}</strong>
                      <ul className="pl-config-list" style={{ marginTop: 4 }}>
                        {actPostes.map(p => (
                          <li key={p.id}>
                            <span>{p.libelle}</span>
                            {isAdmin && <button className="pl-config-delete" onClick={() => handleDeletePoste(p.id)}>x</button>}
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {configTab === 'services' && (
            <>
              {isAdmin && (
                <div className="pl-config-add-row">
                  <select value={newService.activite_id} onChange={e => setNewService(s => ({ ...s, activite_id: e.target.value }))}>
                    <option value="">Activite *</option>
                    {configActivites.map(a => <option key={a.id} value={a.id}>{a.libelle}</option>)}
                  </select>
                  <input value={newService.libelle} onChange={e => setNewService(s => ({ ...s, libelle: e.target.value }))} placeholder="Nom du service" />
                  <button onClick={handleAddService}>Ajouter</button>
                </div>
              )}
              <ul className="pl-config-list">
                {configActivites.map(act => {
                  const actServices = configServices.filter(s => s.activite_id === act.id);
                  if (actServices.length === 0) return null;
                  return (
                    <li key={`act-srv-${act.id}`} className="pl-config-group-header">
                      <strong>{act.libelle}</strong>
                      <ul className="pl-config-list" style={{ marginTop: 4 }}>
                        {actServices.map(s => (
                          <li key={s.id}>
                            <span>{s.libelle}</span>
                            {isAdmin && <button className="pl-config-delete" onClick={() => handleDeleteService(s.id)}>x</button>}
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {configTab === 'groupes' && (
            <>
              <p className="pl-config-aide">
                Un groupe rassemble plusieurs concessions pour les analyser ensemble. Il apparait
                alors dans le selecteur Societe des ecrans P&amp;L et Salaries.
              </p>
              {isAdmin && (
                <div className="pl-config-add">
                  <input value={newGroupe} onChange={e => setNewGroupe(e.target.value)}
                    placeholder="Nom du groupe (ex. SOGUAVASOREC)"
                    onKeyDown={e => { if (e.key === 'Enter') handleAddGroupe(); }} />
                  <button onClick={handleAddGroupe}>Ajouter</button>
                </div>
              )}
              <div className="pl-config-list">
                {configGroupes.length === 0 && <div className="pl-config-vide">Aucun groupe.</div>}
                {configGroupes.map(g => (
                  <div className="pl-config-item" key={g.id}>
                    <span style={{ fontWeight: 600 }}>{g.nom}</span>
                    <span className="pl-etab-effectif">
                      {g.societes} concession(s) :{' '}
                      {configSocietes.filter(so => so.groupe_id === g.id).map(so => so.nom).join(', ') || '—'}
                    </span>
                    {isAdmin && (
                      <button className="pl-config-delete" title="Supprimer le groupe"
                        onClick={async () => {
                          if (!window.confirm(`Supprimer le groupe « ${g.nom} » ? Les concessions ne sont pas supprimees, elles sont simplement deliees.`)) return;
                          try { await plApi.deleteGroupe(g.id); loadConfig(); showToast('Groupe supprime'); }
                          catch { showToast('Erreur', 'error'); }
                        }}>x</button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {configTab === 'etablissements' && (
            <>
              <p className="pl-config-aide">
                L'export de paie ne contient aucun nom de concession : seul le code en tete du
                matricule (<code>1060</code>.00272) identifie l'etablissement. Rattachez chaque code
                a sa concession — les salaries concernes sont mis a jour immediatement, et les
                prochains imports s'en serviront.
              </p>
              <div className="pl-config-list">
                {configEtabs.length === 0 && <div className="pl-config-vide">Aucun matricule en base.</div>}
                {configEtabs.map(e => (
                  <div className={`pl-config-item pl-etab-item ${e.sans_societe > 0 ? 'pl-etab-orphelin' : ''}`} key={e.code}>
                    <span className="pl-etab-code">{e.code}</span>
                    <span className="pl-etab-effectif">
                      {e.salaries} salarie(s)
                      {e.sans_societe > 0 && <b> · {e.sans_societe} sans concession</b>}
                    </span>
                    {isAdmin ? (
                      <select value={e.societe_id || ''} onChange={async ev => {
                        const v = ev.target.value ? parseInt(ev.target.value) : null;
                        try {
                          const r = await plApi.saveEtablissement(e.code, { societe_id: v });
                          showToast(r.data.salaries_rattaches > 0
                            ? `${r.data.salaries_rattaches} salarie(s) rattache(s)`
                            : 'Correspondance enregistree');
                          loadConfig();
                        } catch { showToast('Erreur', 'error'); }
                      }}>
                        <option value="">Non rattache</option>
                        {configSocietes.map(so => <option key={so.id} value={so.id}>{so.nom}</option>)}
                      </select>
                    ) : (
                      <span>{e.societe_nom || 'Non rattache'}</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {configTab === 'exercices' && (
            <>
              {isAdmin && (
                <div className="pl-config-add-row">
                  <input type="number" value={newExercice.annee} onChange={e => setNewExercice({ annee: e.target.value })} placeholder="Annee" style={{ width: 120 }} />
                  <button onClick={handleAddExercice}>Ajouter</button>
                </div>
              )}
              <ul className="pl-config-list">
                {configExercices.map(ex => (
                  <li key={ex.id}><span>{ex.annee}</span></li>
                ))}
              </ul>
            </>
          )}
          {configTab === 'comptes' && (
            <>
              {isAdmin && (
                <div className="pl-config-add-row pl-config-add-compte">
                  <input value={newCompte.libelle} onChange={e => setNewCompte(p => ({ ...p, libelle: e.target.value }))} placeholder="Libelle" />
                  <select value={newCompte.type_ligne} onChange={e => setNewCompte(p => ({ ...p, type_ligne: e.target.value }))}>
                    <option value="detail">Detail</option>
                    <option value="sous-total">Sous-total</option>
                    <option value="total">Total</option>
                    <option value="pourcentage">Pourcentage</option>
                  </select>
                  <select value={newCompte.parent_id} onChange={e => setNewCompte(p => ({ ...p, parent_id: e.target.value }))}>
                    <option value="">Pas de parent</option>
                    {configComptes.filter(c => c.type_ligne !== 'detail').map(c => (
                      <option key={c.id} value={c.id}>{c.libelle}</option>
                    ))}
                  </select>
                  <button onClick={handleAddCompte}>Ajouter</button>
                </div>
              )}
              <div className="pl-config-tree">
                {buildTree(configComptes).map(c => renderConfigCompte(c, 0))}
              </div>
            </>
          )}
          {configTab === 'typesrh' && (
            <>
              {isAdmin && (
                <div className="pl-config-add-row">
                  <input value={newTypeRH} onChange={e => setNewTypeRH(e.target.value)} placeholder="Libelle du type" />
                  <button onClick={handleAddTypeRH}>Ajouter</button>
                </div>
              )}
              <ul className="pl-config-list">
                {configTypesRH.map(t => (
                  <li key={t.id}>
                    <span>{t.libelle}</span>
                    {isAdmin && <button className="pl-config-delete" onClick={() => handleDeleteTypeRH(t.id)}>x</button>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="pl-container">
      {toast && (
        <div className={`pl-toast ${toast.type}`}>
          {toast.msg}
          {toast.undoAction && (
            <button className="toast-undo-btn" onClick={() => { toast.undoAction(); setToast(null); }}>
              Annuler
            </button>
          )}
        </div>
      )}

      <div className="pl-filters-bar">
        <div className="pl-filter-group">
          <label>Exercice</label>
          <select value={selectedExercice} onChange={e => setSelectedExercice(e.target.value)}>
            {exercices.map(ex => <option key={ex.id} value={ex.id}>{ex.annee}</option>)}
          </select>
        </div>
        <div className="pl-filter-group">
          <label>Societe(s)</label>
          <select multiple value={selectedSocietes.map(String)} onChange={e => setSelectedSocietes([...e.target.selectedOptions].map(o => Number(o.value)))}>
            {/* Regroupees par groupe : selectionner un bloc entier revient a analyser le groupe */}
            {(() => {
              const parGroupe = new Map();
              societes.forEach(so => {
                const cle = so.groupe_nom || null;
                if (!parGroupe.has(cle)) parGroupe.set(cle, []);
                parGroupe.get(cle).push(so);
              });
              const blocs = [...parGroupe.entries()].sort((a, b) => (a[0] === null ? 1 : b[0] === null ? -1 : a[0].localeCompare(b[0])));
              return blocs.map(([groupe, liste]) => (
                groupe
                  ? <optgroup key={groupe} label={groupe}>
                      {liste.map(so => <option key={so.id} value={so.id}>{so.nom}</option>)}
                    </optgroup>
                  : liste.map(so => <option key={so.id} value={so.id}>{so.nom}</option>)
              ));
            })()}
          </select>
        </div>
        <div className="pl-filter-group">
          <label>Activite</label>
          <select value={selectedActivite} onChange={e => setSelectedActivite(e.target.value)}>
            <option value="global">Global</option>
            {activites.map(a => <option key={a.id} value={a.id}>{a.libelle || a.code}</option>)}
          </select>
        </div>
        {activeTab === 'reporting' && <button className="pl-config-btn" onClick={handleExportPL}>Imprimer P&L</button>}
        {isAdmin && <button className="pl-config-btn" onClick={openConfig}>Configuration</button>}
      </div>

      <div className="pl-tabs">
        {[
          { id: 'reporting', label: 'Reporting P&L' },
          { id: 'fournisseurs', label: 'Detail fournisseurs' },
          { id: 'salaries', label: 'Masse salariale' }
        ].map(t => (
          <button key={t.id} className={`pl-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="pl-loading">Chargement des donnees...</div>
      ) : (
        <>
          {activeTab === 'reporting' && renderReporting()}
          {activeTab === 'fournisseurs' && renderFournisseurs()}
          {activeTab === 'salaries' && renderSalaries()}
        </>
      )}

      {configOpen && renderConfigPanel()}
    </div>
  );
}
